<template>
  <div class="space-y-4">
    <!-- Main grid: Users/Teams, Criteria, History -->
    <div class="grid grid-cols-1 lg:gap-4 lg:auto-rows-fr transition-all" :class="anonymousMode ? 'lg:grid-cols-2' : 'lg:grid-cols-3'" style="height: 35vh">
      <!-- Left: User List or Team List (hidden in anonymous mode) -->
      <UserList
        v-if="!anonymousMode && !teamMode"
        ref="userList"
        class="min-h-0"
        :users="users"
        :selected="selectedUserId"
        @select="selectUser"
        :is-focused="keyboardFocus === 'users'"
      />
      
      <TeamList
        v-if="!anonymousMode && teamMode"
        ref="teamList"
        class="min-h-0"
        :teams="teams"
        :selected="selectedTeamId"
        :sessionUsers="sessionUsers"
        @select="selectTeam"
        :is-focused="keyboardFocus === 'users'"
      />

      <!-- Second: Criteria List -->
      <UserCriteriaList
        ref="list"
        class="min-h-0"
        :criteria="criteria"
        :selected="selectedCriterion"
        @select="selectCriterion"
        :is-focused="keyboardFocus === 'list'"
      />

      <!-- Right: History -->
      <UserCriterionHistory
        ref="history"
        class="min-h-0"
        :criterion="selectedCriterion"
        :allHistories="allHistoriesFlattened"
        :history-index-from-parent="historyIndex"
        :is-focused="keyboardFocus === 'history'"
        :teamMode="teamMode"
        :teamId="selectedTeamId"
        :teamName="selectedTeamName"
        :userId="userId"
        :sessionId="sessionId"
        @updated="$emit('updated')"
      />
    </div>

    <!-- Assessment Modal: opened via Enter on a selected criterion -->
    <AssessmentModal
      :isOpen="assessmentModalOpen"
      :criterion="selectedCriterion"
      :criteria="criteria"
      :subjectName="assessmentSubjectName"
      :anonymousMode="anonymousMode"
      :teamMode="teamMode"
      :userId="userId"
      :teamId="selectedTeamId"
      :sessionId="sessionId"
      @close="closeAssessmentModal"
      @updated="$emit('updated')"
    />
  </div>
</template>

<script>
import UserCriteriaList from "./UserCriteriaList.vue";
import UserCriterionHistory from "./UserCriterionHistory.vue";
import UserList from "./UserList.vue";
import TeamList from "./TeamList.vue";
import AssessmentModal from "./AssessmentModal.vue";

export default {
  components: { UserCriteriaList, UserCriterionHistory, UserList, TeamList, AssessmentModal },
  props: {
    criteria: { type: Array, required: true },
    userId: { type: Number, required: true },
    sessionId: { type: Number, required: true },
    teamMode: { type: Boolean, default: false },
    teamId: { type: Number, default: null },
    users: { type: Array, required: true },    teams: { type: Array, default: () => [] },
    sessionUsers: { type: Array, default: () => [] },    selecteduserId: { type: Number, default: null },
    anonymousMode: { type: Boolean, default: false },
    modalOpen: { type: Boolean, default: false },
  },
  data() {
    return {
      selectedCriterion: null,
      selectedUserId: null,
      selectedTeamId: null,
      keyboardFocus: "users",
      historyIndex: 0,
      assessmentModalOpen: false,
    };
  },
  computed: {
    assessmentSubjectName() {
      if (this.teamMode) return this.selectedTeamName;
      const user = this.users?.find(u => u.id === this.userId);
      return user ? `${user.first_name} ${user.last_name}` : "";
    },
    allHistoriesFlattened() {
      if (!this.criteria || this.criteria.length === 0) return [];
      return this.criteria
        .flatMap(c => (c.history || []).map(h => ({
          ...h,
          criterion_name: c.criterion?.name || "Unknown Criterion"
        })))
        .sort((a, b) => new Date(b.changed_at) - new Date(a.changed_at));
    },
    selectedTeamName() {
      if (!this.selectedTeamId || !this.teams) return '';
      const team = this.teams.find(t => t.id === this.selectedTeamId);
      return team ? team.name : '';
    },
    sortedUsers() {
      return [...(this.users || [])].sort((a, b) => {
        const first = (a.first_name || "").localeCompare(b.first_name || "", "en", { sensitivity: "base" });
        if (first !== 0) return first;
        return (a.last_name || "").localeCompare(b.last_name || "", "en", { sensitivity: "base" });
      });
    },
    sortedTeams() {
      return [...(this.teams || [])].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", "en", { sensitivity: "base" })
      );
    },
  },
  watch: {
    criteria: {
      deep: true,
      handler(newList) {
        if (this.selectedCriterion) {
          if (this.selectedCriterion.all) {
            // Keep "All criteria" selected
            this.selectedCriterion = { all: true };
          } else {
            // Find updated item in the new list
            const updated = newList.find(c => c.criterion_id === this.selectedCriterion.criterion_id);
            if (updated) {
              // Force Vue to detect the change by briefly setting to null
              this.selectedCriterion = null;
              this.$nextTick(() => {
                this.selectedCriterion = updated;
              });
            } else {
              this.selectedCriterion = null;
            }
          }
        }
        if (!this.selectedCriterion && newList.length) {
          const lastSelectedId = localStorage.getItem("lastSelectedCriterionId");
          const last = newList.find(c => c.criterion_id == lastSelectedId);
          this.selectCriterion(last || newList[0]);
        }
      }
    },
    anonymousMode(newVal) {
      // When entering anonymous mode, set focus to list and disable user navigation
      if (newVal) {
        this.keyboardFocus = "list";
      }
    },
    teamMode(newVal) {
      // When switching to team mode, select first team if available
      if (newVal && this.sortedTeams.length > 0 && !this.selectedTeamId) {
        this.selectedTeamId = this.sortedTeams[0].id;
      }
    },
    teams(newVal) {
      // When teams are loaded, select first team if in team mode and nothing selected
      if (this.teamMode && newVal && newVal.length > 0 && !this.selectedTeamId) {
        this.selectedTeamId = this.sortedTeams[0].id;
      }
    }
  },
  methods: {
    selectUser(userId) {
      this.selectedUserId = userId;
      this.$emit("select-user", userId);
    },
    selectTeam(teamId) {
      this.selectedTeamId = teamId;
      // Emit event so parent can fetch criteria for this team
      this.$emit("select-team", teamId);
    },
    selectCriterion(c) {
      this.selectedCriterion = c;
      this.historyIndex = 0;
      if (!c?.all)
        localStorage.setItem("lastSelectedCriterionId", c.criterion_id);
    },

    openAssessmentModal() {
      if (!this.selectedCriterion || this.selectedCriterion.all) return;
      this.assessmentModalOpen = true;
      this.$emit("assessment-modal-change", true);
    },
    closeAssessmentModal() {
      this.assessmentModalOpen = false;
      this.$emit("assessment-modal-change", false);
    },

    handleKey(e) {
      // Don't handle keys if a modal is open
      if (this.modalOpen || this.assessmentModalOpen) return;

      if (!this.criteria || !this.selectedCriterion) return;

      const focusedElement = document.activeElement;
      const isTextArea = focusedElement?.tagName === "TEXTAREA";
      const inputIsFocused = focusedElement?.tagName === "INPUT";
      const numberInputIsFocused = inputIsFocused && focusedElement?.type === "number";
      const textInputIsFocused = inputIsFocused && focusedElement?.type !== "checkbox";
      const buttonIsFocused = focusedElement?.tagName === "BUTTON";
      // Stop the browser from scrolling the whole page
      if (!isTextArea && !numberInputIsFocused) {
        if (["ArrowUp", "ArrowDown"].includes(e.key)) {
          e.preventDefault();
        }
      }

      // --- Open assessment modal on Enter ---
      if (e.key === "Enter" && !isTextArea && !textInputIsFocused && !buttonIsFocused
          && !e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
        if (!this.selectedCriterion.all) {
          e.preventDefault();
          this.openAssessmentModal();
        }
        return;
      }

      // --- Ignore if ALT + T (handled in parent) ---
      if (e.altKey && e.key.toLowerCase() === "t") {
        return;
      }

      // --- Focus switching ---
      if (e.key === "ArrowRight" && !isTextArea) {
        // Cycle: users -> list -> history -> users (skip users in anonymous mode)
        const order = this.anonymousMode ? ["list", "history"] : ["users", "list", "history"];
        const currentIdx = order.indexOf(this.keyboardFocus);
        const nextIdx = (currentIdx + 1) % order.length;
        this.keyboardFocus = order[nextIdx];
        return;
      }
      if (e.key === "ArrowLeft" && !isTextArea) {
        // Cycle: users -> history -> list -> users (skip users in anonymous mode)
        const order = this.anonymousMode ? ["list", "history"] : ["users", "list", "history"];
        const currentIdx = order.indexOf(this.keyboardFocus);
        const nextIdx = (currentIdx - 1 + order.length) % order.length;
        this.keyboardFocus = order[nextIdx];
        return;
      }

      // --- Users list navigation (disabled in anonymous mode) ---
      if (this.keyboardFocus === "users" && !isTextArea && !this.anonymousMode) {
        // Team mode navigation
        if (this.teamMode && this.sortedTeams.length > 0) {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            const currentIdx = this.sortedTeams.findIndex(t => t.id === this.selectedTeamId);
            const nextIdx = Math.min(currentIdx + 1, this.sortedTeams.length - 1);
            this.selectTeam(this.sortedTeams[nextIdx].id);
            this.$nextTick(() => this.$refs.teamList?.scrollToSelected?.());
            return;
          }
          if (e.key === "ArrowUp") {
            e.preventDefault();
            const currentIdx = this.sortedTeams.findIndex(t => t.id === this.selectedTeamId);
            const nextIdx = Math.max(currentIdx - 1, 0);
            this.selectTeam(this.sortedTeams[nextIdx].id);
            this.$nextTick(() => this.$refs.teamList?.scrollToSelected?.());
            return;
          }
        }
        // User mode navigation
        else if (!this.teamMode && this.sortedUsers.length > 0) {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            const currentIdx = this.sortedUsers.findIndex(u => u.id === this.selectedUserId);
            const nextIdx = Math.min(currentIdx + 1, this.sortedUsers.length - 1);
            this.selectUser(this.sortedUsers[nextIdx].id);
            this.$nextTick(() => this.$refs.userList?.scrollToSelected?.());
            return;
          }
          if (e.key === "ArrowUp") {
            e.preventDefault();
            const currentIdx = this.sortedUsers.findIndex(u => u.id === this.selectedUserId);
            const nextIdx = Math.max(currentIdx - 1, 0);
            this.selectUser(this.sortedUsers[nextIdx].id);
            this.$nextTick(() => this.$refs.userList?.scrollToSelected?.());
            return;
          }
        }
      }

      // --- List navigation ---
      if (this.keyboardFocus === "list" && !isTextArea) {
        const fullList = [{ all: true }, ...this.criteria]; // include "All criteria" first
        let idx = fullList.findIndex(c =>
          (c.all && this.selectedCriterion?.all) ||
          c.criterion_id === this.selectedCriterion?.criterion_id
        );
        if (idx === -1) idx = 0;

        if (e.key === "ArrowDown") {
          this.selectCriterion(fullList[Math.min(idx + 1, fullList.length - 1)]);
          this.$nextTick(() => this.$refs.list?.scrollToSelected());
          return;
        }
        if (e.key === "ArrowUp") {
          this.selectCriterion(fullList[Math.max(idx - 1, 0)]);
          this.$nextTick(() => this.$refs.list?.scrollToSelected());
          return;
        }
      }

      // --- History navigation ---
      if (this.keyboardFocus === "history" && !isTextArea) {
        let historyList = [];
        if (this.selectedCriterion?.all) {
          historyList = this.allHistoriesFlattened;
        } else {
          historyList = this.selectedCriterion?.history || [];
        }

        const maxIndex = historyList.length - 1;
        if (e.key === "ArrowDown") this.historyIndex = Math.min(this.historyIndex + 1, maxIndex);
        if (e.key === "ArrowUp") this.historyIndex = Math.max(this.historyIndex - 1, 0);
        this.$nextTick(() => {
          this.$refs.history?.scrollToActive();
        });
      }
    },
  },

  mounted() {
    window.addEventListener("keydown", this.handleKey);

    // Initialize selected user or team
    if (this.teamMode && this.sortedTeams.length > 0) {
      this.selectedTeamId = this.sortedTeams[0].id;
    } else if (this.userId && this.users.length) {
      this.selectedUserId = this.userId;
    }

    // Set initial keyboard focus (skip users list in anonymous mode)
    if (this.anonymousMode) {
      this.keyboardFocus = "list";
    }

    // Default to first criterion
    if (!this.selectedCriterion && this.criteria.length) this.selectCriterion(this.criteria[0]);

    // Load last selected from localStorage
    const lastSelectedId = localStorage.getItem("lastSelectedCriterionId");
    if (lastSelectedId) {
      const last = this.criteria.find(c => c.criterion_id == lastSelectedId);
      if (last) this.selectCriterion(last);
    }
  },

  beforeUnmount() {
    window.removeEventListener("keydown", this.handleKey);
  },
};
</script>
